import { FC, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Medal, Star } from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from '@/lib/utils';
import { useBadges, Badge } from '@/hooks/useBadges';
import { useProfile } from '@/hooks/useProfile';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export const MedalsCollection: FC = () => {
  const { data: badges, isLoading } = useBadges();
  const { userProfile } = useProfile();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Medal className="w-6 h-6 text-primary" />
          <h2 className="text-xl font-bold">Colección de Medallas</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-square rounded-xl bg-primary/10"></div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  // Calcular el progreso hacia el siguiente nivel
  const calculateProgress = (level: number): number => {
    const currentLevel = userProfile?.level || 1;
    const currentCerebrix = userProfile?.cerebrix || 0;
    const cerebrixPerLevel = 100; // Asumiendo que cada nivel requiere 100 Cerebrix

    if (level < currentLevel) {
      return 100; // Niveles ya completados
    }

    if (level === currentLevel) {
      // Para el nivel actual, calculamos el progreso basado en los Cerebrix actuales
      return Math.min(100, ((currentCerebrix % cerebrixPerLevel) / cerebrixPerLevel) * 100);
    }

    return 0; // Niveles futuros
  };

  const BadgeInfo: FC<{ badge: Badge; progress: number }> = ({ badge, progress }) => (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-4">
        <div className="text-4xl">{badge.badge_icon}</div>
        <div>
          <h4 className="font-semibold text-lg">{badge.badge_name}</h4>
          <p className="text-sm text-muted-foreground">Nivel {badge.level}</p>
        </div>
      </div>
      <p className="text-sm">{badge.badge_description}</p>
      <div className="space-y-2">
        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-xs text-right text-muted-foreground">
          Progreso: {progress.toFixed(0)}%
        </p>
      </div>
    </div>
  );

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Medal className="w-6 h-6 text-primary" />
        <h2 className="text-xl font-bold">Colección de Medallas</h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {badges?.map((badge, index) => {
          const currentLevel = userProfile?.level || 1;
          const isUnlocked = currentLevel >= badge.level;
          const progress = calculateProgress(badge.level);

          const badgeCard = (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className={cn(
                "relative group cursor-pointer",
                isUnlocked ? "bg-gradient-to-br from-primary/10 to-secondary/10" : "bg-gray-100",
                "rounded-xl overflow-hidden"
              )}
              onClick={() => isMobile && setSelectedBadge(badge)}
            >
              <div className="aspect-square flex flex-col items-center justify-center p-4 transition-all duration-300 group-hover:shadow-lg">
                <div className={cn(
                  "text-4xl mb-2",
                  isUnlocked ? "text-primary" : "text-gray-400"
                )}>
                  {badge.badge_icon}
                </div>
                <h3 className={cn(
                  "text-sm font-medium text-center",
                  isUnlocked ? "text-primary" : "text-gray-500"
                )}>
                  {badge.badge_name}
                </h3>
                {isUnlocked && (
                  <div className="absolute top-2 right-2">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  </div>
                )}
                {/* Barra de progreso */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 1, delay: index * 0.1 }}
                    className={cn(
                      "h-full",
                      isUnlocked ? "bg-primary" : "bg-gray-300"
                    )}
                  />
                </div>
              </div>
            </motion.div>
          );

          return isMobile ? (
            <div key={badge.id}>{badgeCard}</div>
          ) : (
            <TooltipProvider key={badge.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  {badgeCard}
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-white p-0 w-80">
                  <BadgeInfo badge={badge} progress={progress} />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>

      {/* Modal para móviles */}
      <Dialog open={!!selectedBadge} onOpenChange={() => setSelectedBadge(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Información de la Medalla</DialogTitle>
            <DialogDescription asChild>
              {selectedBadge && (
                <BadgeInfo
                  badge={selectedBadge}
                  progress={calculateProgress(selectedBadge.level)}
                />
              )}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      {/* Texto explicativo */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
        <h3 className="font-semibold mb-2">¿Cómo funcionan las medallas?</h3>
        <p className="mb-2">
          Las medallas representan tu progreso en FinancieraMente. Cada nivel desbloqueado te otorga una nueva medalla que refleja tu crecimiento en educación financiera.
        </p>
        <p>
          ¡Completa lecciones y acumula Cerebrix para desbloquear todas las medallas! Cada medalla es un símbolo de tu dominio en diferentes aspectos financieros. 🎯
        </p>
      </div>
    </Card>
  );
};
